import React from "react";
import { useParams } from "react-router-dom";
import { BsCalendar, BsClock, BsJournalText } from "react-icons/bs";
import SectionLayout from "../common/SectionLayout";
import ArticleContent from "./ArticleContent";
import TableOfContents from "./TableOfContents";
import ReadingProgress from "./ReadingProgress";
import BackToTop from "./BackToTop";
import ShareButtons from "./ShareButtons";
import RelatedArticles from "./RelatedArticles";
import Breadcrumb from "../common/Breadcrumb";
import useArticleLoader from "../../hooks/useArticleLoader";
import { useTableOfContents } from "../../hooks/useTableOfContents";
import { useActiveSection } from "../../hooks/useActiveSection";
import { ArticleContentSkeleton } from "../common/LoadingSkeleton";

function ArticleReader() {
  const { articleId } = useParams();
  const { article, content, loading, error } = useArticleLoader(articleId);
  
  // Extract headings for TOC
  const headings = useTableOfContents(content);
  const headingIds = headings.map(h => h.id);
  const activeId = useActiveSection(headingIds);

  if (loading) {
    return (
      <SectionLayout className="article-reader-section">
        <div className="article-reader-container">
          <div className="toc-sidebar" style={{ opacity: 0.5 }}>
            <div className="skeleton" style={{ height: "30px", marginBottom: "1rem" }} />
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: "20px", marginBottom: "0.5rem", width: "80%" }} />
            ))}
          </div>
          <div className="article-main-content">
            <ArticleContentSkeleton />
          </div>
        </div>
      </SectionLayout>
    );
  }

  if (error) {
    return (
      <SectionLayout className="article-section">
        <h1 style={{ color: "white", textAlign: "center", marginTop: "100px" }}>
          {error}
        </h1>
        <div style={{ textAlign: "center", marginTop: "20px" }}>
        </div>
      </SectionLayout>
    );
  }

  return (
    <SectionLayout className="article-reader-section">
      {/* Reading Progress Bar */}
      <ReadingProgress />
      
      {/* Back to Top Button */}
      <BackToTop />
      
      {/* Breadcrumb Navigation */}
      <Breadcrumb
        customItems={[
          { label: 'Home', link: '/', icon: <BsJournalText /> },
          { label: 'Blog', link: '/articles' },
          { label: article.title, link: null }
        ]}
      />
      
      <div className="article-reader-container">
        {/* Table of Contents */}
        <TableOfContents headings={headings} activeId={activeId} />

        {/* Main Content */}
        <div className="article-main-content content-loaded">
          <div className="article-header">
            <img
              src={article.coverImage}
              alt={article.title}
              className="article-cover-image"
            />
            <h1 className="article-title">{article.title}</h1>
            <div className="article-meta">
              <span className="article-author">By {article.author}</span>
              <span className="article-date">
                <BsCalendar /> {new Date(article.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="article-read-time">
                <BsClock /> {article.readTime}
              </span>
            </div>
            <div className="article-tags-header">
              {article.tags.map((tag, index) => (
                <span key={index} className="article-tag-header">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <ArticleContent content={content} />
          
          {/* Share Buttons */}
          <ShareButtons article={article} />
          
          {/* Related Articles */}
          <RelatedArticles currentArticle={article} />
        </div>
      </div>
    </SectionLayout>
  );
}

export default ArticleReader;